import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import { useAppTour } from "@/hooks/useAppTour";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { User, Mail, Calendar, CreditCard, Play, Building2, Plug, Key, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UserData } from "@/types/user";
import { OrganizationSection } from "@/components/settings/OrganizationSection";
import { BillingSection } from "@/components/settings/BillingSection";
import { IntegrationsSection } from "@/components/integrations/IntegrationsSection";
import { ApiKeysSection } from "@/components/settings/ApiKeysSection";
import { ManagedTenantsSection } from "@/components/settings/ManagedTenantsSection";
import { useTenant } from "@/providers/TenantProvider";

type SettingsSection = "profile" | "organization" | "managed-tenants" | "billing" | "api-keys" | "integrations";

const settingsSections: Array<{
  id: SettingsSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredProduct?: string;
  msspOnly?: boolean;
}> = [
  { id: "profile", label: "Profile", icon: User },
  { id: "organization", label: "Organization", icon: Building2 },
  { id: "managed-tenants", label: "Managed Tenants", icon: Users, msspOnly: true },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "api-keys", label: "API Keys", icon: Key },
  { id: "integrations", label: "Integrations", icon: Plug, requiredProduct: "security" },
];

/**
 * Render the profile details card for a user.
 *
 * Displays the user's avatar (or a placeholder), name, username, email, and an optional "Member Since" date.
 *
 * @param userData - The user record used to populate avatar, display name, username, email, and created_at (membership date).
 * @returns A React element containing a card with the user's profile information and related UI elements.
 */
function ProfileSection({ userData }: { userData: UserData }) {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-[15px] shadow-none">
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Profile Picture */}
            <div className="flex items-center gap-4">
              {userData.profile_image_url &&
              userData.profile_image_url !== "https://profileimageurl.com" ? (
                <img
                  src={userData.profile_image_url}
                  alt={userData.name}
                  className="h-24 w-24 rounded-full object-cover border-2 border-border shadow-sm"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border-2 border-border shadow-sm">
                  <User className="h-12 w-12 text-primary/60" />
                </div>
              )}
              <div>
                <h3 className="text-2xl font-semibold tracking-tight">{userData.name}</h3>
                <p className="text-muted-foreground mt-1">@{userData.username}</p>
              </div>
            </div>

            <Separator />

            {/* User Details Grid */}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  Email
                </div>
                <p className="text-base font-medium">{userData.email}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <User className="h-4 w-4" />
                  Username
                </div>
                <p className="text-base font-medium">@{userData.username}</p>
              </div>

              {userData.created_at && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Member Since
                  </div>
                  <p className="text-base font-medium">{formatDate(userData.created_at)}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Render the Settings page with a left navigation for sections and a main area showing profile or billing content.
 *
 * Synchronizes the active section with the URL search param `section`, fetches the current user and (when applicable) tier details, and displays loading, error, profile, or billing states accordingly.
 *
 * @returns The page's JSX element containing the header, sidebar navigation, and main content for settings.
 */
export function SettingsPage() {
  const api = useApi();
  const { restartTour } = useAppTour();
  const { products } = useFeatureFlags();
  const { isMssp } = useTenant();
  const { refetchOrganization } = useOrganizationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleSections = settingsSections.filter(
    (s) =>
      (!s.requiredProduct || products.includes(s.requiredProduct)) &&
      (!s.msspOnly || isMssp)
  );

  // Get section from URL or default to profile
  const sectionParam = searchParams.get("section") as SettingsSection | null;
  const activeSection: SettingsSection =
    sectionParam && visibleSections.some((s) => s.id === sectionParam) ? sectionParam : "profile";

  const setActiveSection = (section: SettingsSection) => {
    setSearchParams({ section });
  };

  // Refetch organization when viewing Billing so tier changes (e.g. from admin) are reflected
  useEffect(() => {
    if (activeSection === "billing") {
      refetchOrganization();
    }
  }, [activeSection, refetchOrganization]);

  const {
    data: userData,
    isLoading,
    error,
  } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const data = await api.get<UserData>("/api/v1/user/me/");
      return data;
    },
    retry: 1,
  });

  return (
    <div className="flex flex-1 flex-col bg-muted/10 px-4 py-6 lg:px-6 [&_.rounded-md]:rounded-[10px] [&_.rounded-lg]:rounded-[10px]">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium">Settings</h1>
            <p className="text-sm text-muted-foreground">
              Manage your account, organization, billing, keys, and integrations
            </p>
          </div>
          <Button
            variant="outline"
            onClick={restartTour}
            className="flex items-center gap-2 rounded-full bg-white"
          >
            <Play className="h-4 w-4" />
            Take a tour
          </Button>
        </div>
      </div>

      {/* Sidebar and Content */}
      <div className="flex flex-1 gap-6">
        {/* Sidebar Navigation */}
        <aside className="w-64 shrink-0">
          <div className="sticky top-6 rounded-[15px] border bg-white p-2">
            <nav className="space-y-1">
              {visibleSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          {isLoading ? (
            <div className="space-y-6">
              <Skeleton className="h-8 w-48" />
              <Card className="rounded-[15px] shadow-none">
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-24 w-24 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-6 w-32" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </div>
                    <Separator />
                    <div className="grid gap-6 md:grid-cols-2">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : error ? (
            <Card className="rounded-[15px] border-destructive shadow-none">
              <CardHeader>
                <CardTitle className="text-destructive">Error Loading Settings</CardTitle>
                <CardDescription>
                  {error instanceof Error ? error.message : "Failed to load user data"}
                </CardDescription>
              </CardHeader>
            </Card>
          ) : userData ? (
            <>
              {activeSection === "profile" && <ProfileSection userData={userData} />}
              {activeSection === "organization" && <OrganizationSection />}
              {activeSection === "managed-tenants" && <ManagedTenantsSection />}
              {activeSection === "billing" && <BillingSection />}
              {activeSection === "api-keys" && <ApiKeysSection />}
              {activeSection === "integrations" && <IntegrationsSection />}
            </>
          ) : null}
        </main>
      </div>
    </div>
  );
}
